import type { ChartConfig } from "@/components/ui/chart";
import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import * as React from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  LabelList,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";

interface HistoryChartProps {
  data: {
    date: string | Date;
    e1RM: number;
  }[];
  label?: string;
}

const chartConfig = {
  e1RM: {
    label: "Tahmini 1RM",
    color: "hsl(var(--primary))",
  },
} satisfies ChartConfig;

export function HistoryChart({ data }: HistoryChartProps) {
  // Sort data by date ascending for the chart
  const sortedData = React.useMemo(() => {
    return [...data].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [data]);

  const stats = React.useMemo(() => {
    if (sortedData.length === 0) return null;
    const bestEver = Math.max(...sortedData.map((d) => d.e1RM));
    const first = sortedData[0].e1RM;
    const last = sortedData[sortedData.length - 1].e1RM;
    const totalProgress = ((last - first) / first) * 100;

    return {
      bestEver,
      first,
      last,
      totalProgress: totalProgress.toFixed(1),
      isPositive: last >= first,
    };
  }, [sortedData]);

  if (sortedData.length === 0 || !stats) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-lg border border-dashed text-muted-foreground text-sm">
        Henüz gelişim verisi bulunmuyor.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Analytical Summary Header */}
      <div className="grid grid-cols-3 divide-x border-border/40 border-b pb-4">
        <div className="flex flex-col px-4 first:pl-0">
          <span className="font-bold text-[10px] text-muted-foreground/50 uppercase tracking-widest">
            Başlangıç
          </span>
          <span className="font-black text-foreground text-xl tracking-tight">
            {stats.first}
            <span className="ml-1 text-[10px] uppercase opacity-30">kg</span>
          </span>
        </div>
        <div className="flex flex-col px-4">
          <span className="font-bold text-[10px] text-muted-foreground/50 uppercase tracking-widest">
            Güncel
          </span>
          <span className="font-black text-foreground text-xl tracking-tight">
            {stats.last}
            <span className="ml-1 text-[10px] uppercase opacity-30">kg</span>
          </span>
        </div>
        <div className="flex flex-col px-4 last:pr-0">
          <span className="font-bold text-[10px] text-muted-foreground/50 uppercase tracking-widest">
            Değişim
          </span>
          <span
            className={cn(
              "font-black text-xl tracking-tight",
              stats.isPositive ? "text-emerald-500" : "text-rose-500",
            )}
          >
            {stats.isPositive ? "+" : ""}
            {stats.totalProgress}%
          </span>
        </div>
      </div>

      <div className="relative">
        <div className="absolute top-0 right-0 flex items-center gap-4 px-2">
          <div className="flex items-center gap-1.5 opacity-50 transition-opacity hover:opacity-100">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span className="font-bold text-[10px] text-muted-foreground uppercase tracking-wider">
              e1RM (kg)
            </span>
          </div>
          {stats.bestEver > 0 && (
            <div className="flex items-center gap-1.5 opacity-50 transition-opacity hover:opacity-100">
              <div className="h-1.5 w-1.5 rounded-full bg-primary/30" />
              <span className="font-bold text-[10px] text-muted-foreground uppercase tracking-wider">
                PR: {stats.bestEver}kg
              </span>
            </div>
          )}
        </div>

        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[280px] w-full"
        >
          <AreaChart
            data={sortedData}
            margin={{
              left: 0,
              right: 12,
              top: 40,
              bottom: 0,
            }}
          >
            <defs>
              <linearGradient id="fillE1RM" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor="var(--color-e1RM)"
                  stopOpacity={0.15}
                />
                <stop
                  offset="95%"
                  stopColor="var(--color-e1RM)"
                  stopOpacity={0.01}
                />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              strokeDasharray="3 3"
              strokeOpacity={0.05}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              minTickGap={32}
              tickFormatter={(value) =>
                format(new Date(value), "d MMM", { locale: tr })
              }
              className="font-bold text-[9px] text-muted-foreground/40 uppercase"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              domain={["dataMin - 10", "dataMax + 10"]}
              tickMargin={10}
              className="font-bold text-[9px] text-muted-foreground/30"
              width={50}
            />
            {stats.bestEver > 0 && (
              <ReferenceLine
                y={stats.bestEver}
                stroke="var(--color-e1RM)"
                strokeDasharray="4 4"
                strokeOpacity={0.2}
              />
            )}
            <ChartTooltip
              cursor={{
                stroke: "hsl(var(--primary))",
                strokeWidth: 2,
                strokeDasharray: "4 4",
                opacity: 0.1,
              }}
              content={<ChartTooltipTooltipContent prevData={sortedData} />}
            />
            <Area
              dataKey="e1RM"
              type="monotone"
              fill="url(#fillE1RM)"
              stroke="var(--color-e1RM)"
              strokeWidth={3}
              animationDuration={1500}
              strokeLinecap="round"
              dot={<CustomDot />}
              activeDot={{
                r: 6,
                strokeWidth: 4,
                stroke: "hsl(var(--background))",
                fill: "var(--color-e1RM)",
                className: "shadow-2xl",
              }}
            >
              <LabelList
                dataKey="e1RM"
                position="top"
                offset={15}
                content={(props: any) => {
                  const { x, y, value, index } = props;
                  const isLast = index === sortedData.length - 1;
                  const isBest = value === stats.bestEver;

                  if (!isLast && !isBest) return null;

                  return (
                    <g>
                      <text
                        x={x}
                        y={y - 8}
                        fill={isBest ? "var(--color-e1RM)" : "currentColor"}
                        className="font-black text-[10px] tabular-nums"
                        textAnchor="middle"
                        opacity={isBest ? 1 : 0.6}
                      >
                        {value}
                      </text>
                      {isBest && (
                        <text
                          x={x}
                          y={y - 20}
                          fill="var(--color-e1RM)"
                          className="font-bold text-[8px] uppercase tracking-tighter"
                          textAnchor="middle"
                        >
                          PR
                        </text>
                      )}
                    </g>
                  );
                }}
              />
            </Area>
          </AreaChart>
        </ChartContainer>
      </div>
    </div>
  );
}

function CustomDot(props: any) {
  const { cx, cy, index, data } = props;
  if (!data) return null;
  const isLast = index === data.length - 1;

  if (!isLast) return null;

  return (
    <g>
      <circle
        cx={cx}
        cy={cy}
        r={12}
        fill="var(--color-e1RM)"
        className="animate-pulse opacity-10"
      />
      <circle
        cx={cx}
        cy={cy}
        r={6}
        stroke="hsl(var(--background))"
        strokeWidth={3}
        fill="var(--color-e1RM)"
        className="shadow-xl"
      />
    </g>
  );
}

function ChartTooltipTooltipContent({ active, payload, prevData }: any) {
  if (!active || !payload?.length) return null;

  const data = payload[0].payload;
  const currentIndex = prevData.findIndex((d: any) => d.date === data.date);
  const prevValue = currentIndex > 0 ? prevData[currentIndex - 1].e1RM : null;
  const diff = prevValue ? data.e1RM - prevValue : null;

  return (
    <div className="min-w-[160px] overflow-hidden rounded-2xl border border-border/50 bg-background/95 p-0 shadow-2xl backdrop-blur-md">
      <div className="border-border/40 border-b bg-muted/30 px-3 py-2">
        <div className="flex items-center justify-between gap-4">
          <span className="whitespace-nowrap font-black text-[10px] text-muted-foreground/60 uppercase tracking-widest">
            {data.weekNumber && data.dayNumber
              ? `Hafta ${data.weekNumber} Gün ${data.dayNumber}`
              : "Antrenman"}
          </span>
          {diff !== null && (
            <span
              className={cn(
                "flex items-center rounded-full px-1.5 font-bold text-[10px]",
                diff > 0
                  ? "bg-emerald-500/10 text-emerald-600"
                  : diff < 0
                    ? "bg-rose-500/10 text-rose-600"
                    : "bg-muted text-muted-foreground",
              )}
            >
              {diff > 0 ? "+" : ""}
              {diff}
            </span>
          )}
        </div>
        <div className="mt-0.5 font-bold text-foreground/80 text-xs leading-none">
          {format(new Date(data.date), "d MMMM yyyy", { locale: tr })}
        </div>
      </div>
      <div className="p-3">
        <div className="flex flex-col">
          <span className="mb-1 font-bold text-[9px] text-muted-foreground/40 uppercase tracking-widest">
            Tahmini 1RM
          </span>
          <div className="flex items-baseline gap-1.5">
            <span className="font-black text-2xl text-primary tabular-nums leading-none tracking-tighter">
              {payload[0].value}
            </span>
            <span className="font-bold text-[10px] text-muted-foreground uppercase opacity-40">
              kg
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
